import { useReducer, useState, useEffect, useContext } from "react";
import DevModeLabel from "../../components/ui/labels/DevModeLabel";
import SectionHeader from "../../components/ui/labels/SectionHeader";
import { ACTIONS, initialState, reducer } from "./splicerAuditReducer";
import useGroupMemberships from "../../hooks/useGroupMemberships";
import { useNavigate, useLocation } from "react-router-dom";
import useAPIGetData from "../../hooks/useAPIGetData";
import FullScreenActivityIndicator from "../../components/ui/activityIndicator/FullScreenActivityIndicator";
import InfoPrompt from "../../components/ui/prompts/InfoPrompt";
import CenterCard from "../../components/ui/containers/Card";
import Button from "../../components/ui/buttons/Button";
import Form from "../../components/ui/input/Form";
import CurrentUser from "../../components/ui/labels/CurrentUser";
import CurrentDate from "../../components/ui/labels/CurrentDate";
import LatLong from "../../components/ui/labels/LatLong";
import FormPage1 from "./FormPage1";
import FormPage2 from "./FormPage2";
import ImagePreviewPrompt from "../../components/ui/prompts/ImagePreviewPrompt";
import AuthContext from "../../components/context/AuthContext";
import axios from "axios";
import Switch from "../../components/ui/switch/Switch";
import ToolInventory from "./ToolInventory";

const SplicerAudit = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const groupMemberships = useGroupMemberships();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [auditCoords] = useState(routeLocation.state);
  const authCtx = useContext(AuthContext);

  // const auditCoords = {
  //   latitude: 29.60055,
  //   longitude: -81.244821,
  // };

  const { loading, error, data } = useAPIGetData(
    `${process.env.REACT_APP_API_URL}/audit/splicerQuestions`
  );

  useEffect(() => {
    let hasAccess =
      groupMemberships.isPrimary ||
      groupMemberships.isAuditorSplicer ||
      groupMemberships.isAuditPrimary;
    if (!hasAccess) {
      cancelHandler();
    }
    // eslint-disable-next-line
  }, [groupMemberships]);

  /*
    Fetch question data containing objects with each question's
    GUID and question. 
  */
  useEffect(() => {
    if (error) {
      dispatch({
        type: ACTIONS.SET_STATUS,
        value: error,
      });
    }
    if (data) {
      try {
        data.length === 0
          ? dispatch({
              type: ACTIONS.SET_STATUS,
              value: "No question data found.",
            })
          : dispatch({
              type: ACTIONS.SET_QUESTIONS,
              value: data,
            });
      } catch {
        dispatch({
          type: ACTIONS.SET_STATUS,
          value: "Failed to retrieve question data.",
        });
      }
    }
  }, [data, error]);

  const cancelHandler = () => {
    navigate("/home", {
      replace: true,
      state: {
        latitude: auditCoords.latitude,
        longitude: auditCoords.longitude,
      },
    });
  };

  const clearStatus = () => {
    dispatch({
      type: ACTIONS.SET_STATUS,
      value: "",
    });
  };

  const setPreviewHandler = (photo) => {
    dispatch({
      type: ACTIONS.SET_PREVIEW,
      value: photo,
    });
  };

  const processQuestions = () => {
    let obj = [
      state.fiberSplicing,
      state.housekeeping,
      state.requiredPPE,
      state.requiredWAP,
    ];
    return JSON.stringify(obj);
  };

  const processToolInventory = () => {
    if (state.showToolInventory === false) return null;
    let obj = {
      bareFiberAdapterMakeModel: state.bareFiberAdapterMakeModel,
      bufferTubeMakeModel: state.bufferTubeMakeModel,
      conesMakeModel: state.conesMakeModel,
      coreTubeEntryKitMakeModel: state.coreTubeEntryKitMakeModel,
      drill: state.drill,
      exfoLaunchBagMakeModel: state.exfoLaunchBagMakeModel,
      fiberMakeModel: state.fiberMakeModel,
      fiberSerial: state.fiberSerial,
      fishTapeMakeModel: state.fishTapeMakeModel,
      foDropSlitterMakeModel: state.foDropSlitterMakeModel,
      fsMachineRibbonMakeModel: state.fsMachineRibbonMakeModel,
      fsMachineRibbonSerial: state.fsMachineRibbonSerial,
      fsMachineSingleMakeModel: state.fsMachineSingleMakeModel,
      fsMachineSingleSerial: state.fsMachineSingleSerial,
      handToolsMakeModel: state.handToolsMakeModel,
      harnessMakeModel: state.harnessMakeModel,
      ladder28ftMakeModel: state.ladder28ftMakeModel,
      ladder6ftMakeModel: state.ladder6ftMakeModel,
      midspanSplitterMakeModel: state.midspanSplitterMakeModel,
      opticalLossMakeModel1: state.opticalLossMakeModel1,
      opticalLossMakeModel2: state.opticalLossMakeModel2,
      opticalLossSerial1: state.opticalLossSerial1,
      opticalLossSerial2: state.opticalLossSerial2,
      otdrMakeModel: state.otdrMakeModel,
      otdrSerial: state.otdrSerial,
      safetyVestAndHardHatMakeModel: state.safetyVestAndHardHatMakeModel,
      signAndHolderMakeModel: state.signAndHolderMakeModel,
      splicerTentMakeModel: state.splicerTentMakeModel,
      splicingTableMakeModel: state.splicingTableMakeModel,
      toolNotes: state.toolNotes,
      toolsetMakeModel: state.toolsetMakeModel,
      trailerTypeAndNum: state.trailerTypeAndNum,
      vehicleNumber: state.vehicleNumber,
      vehicleType: state.vehicleType,
    };
    return JSON.stringify(obj);
  };

  const switchToggleHandler = () => {
    dispatch({
      type: ACTIONS.TOOL_INVENTORY_TOGGLE,
    });
    dispatch({ type: ACTIONS.SET_FORM_VALID });
  };

  const addPhotos = (photoList, photos) => {
    if (photos && photos.length > 0) {
      for (let photo of photos) {
        photoList.push(photo);
      }
    }
  };

  const attachQuestionPhotos = () => {
    let photoList = [];
    addPhotos(photoList, state.fiberSplicing.photos);
    addPhotos(photoList, state.housekeeping.photos);
    addPhotos(photoList, state.requiredPPE.photos);
    addPhotos(photoList, state.requiredWAP.photos);
    return JSON.stringify(photoList);
  };

  const constructFormDataObject = () => {
    let formData = new FormData();
    formData.append("conductedBy", authCtx?.user?.username);
    formData.append("email", authCtx?.user?.email);
    formData.append("latitude", `${auditCoords.latitude}`);
    formData.append("longitude", `${auditCoords.longitude}`);
    formData.append("market", state.selectedMarket?.value?.GLOBALID);
    formData.append("areaname", state.selectedLCP?.value?.GLOBALID);
    formData.append(
      "contractor",
      state.selectedContractor?.value?.MCA_NUMBER
        ? state.selectedContractor.value.MCA_NUMBER
        : "N/A"
    );
    formData.append(
      "crew",
      state.selectedCrew?.value?.CREW_GUID
        ? state.selectedCrew.value.CREW_GUID
        : "N/A"
    );
    formData.append(
      "inHouseSplicer",
      state.selectedInHouseSplicer?.value?.sAMAccountName
        ? state.selectedInHouseSplicer.value.sAMAccountName
        : "N/A"
    );
    formData.append(
      "inHouseSplicerEmail",
      state.selectedInHouseSplicer?.value?.email
        ? state.selectedInHouseSplicer.value.email
        : "N/A"
    );
    let questionObjectJSON = processQuestions();
    formData.append("questionData", questionObjectJSON);
    formData.append("questionPhotos", attachQuestionPhotos());
    let toolObjectJSON = processToolInventory();
    if (toolObjectJSON) {
      formData.append("toolInventory", toolObjectJSON);
    }
    return formData;
  };

  const submitAuditHandler = async (event) => {
    event.preventDefault();
    dispatch({
      type: ACTIONS.LOADING_TOGGLE,
    });
    let formData = constructFormDataObject();
    let url = `${process.env.REACT_APP_API_URL}/audit/createSplicerAudit`;
    let authObj = { headers: { Authorization: `token ${authCtx.token}` } };
    axios
      .post(url, formData, authObj)
      .then((response) => {
        submissionResponseHandler(response);
      })
      .catch((error) => {
        submissionResponseHandler(error);
      });
  };

  // Updates state from the submission response handler.
  const submissionResponseHandler = (response) => {
    response && response.data
      ? dispatch({
          type: ACTIONS.SET_SUBMISSION_STATUS,
          value: "Audit submitted successfully!",
        })
      : dispatch({
          type: ACTIONS.SET_STATUS,
          value: `Submission error: Invalid response returned: ${response?.message}`,
        });
  };

  const formPage1Completed = () => {
    if (groupMemberships.seeINHS) {
      return (
        state.selectedMarket.value &&
        state.selectedLCP.value &&
        ((state.selectedContractor.value && state.selectedCrew.value) ||
          state.selectedInHouseSplicer.value)
      );
    }
    return (
      state.selectedMarket.value &&
      state.selectedLCP.value &&
      state.selectedContractor.value &&
      state.selectedCrew.value
    );
  };

  return (
    <>
      <SectionHeader text="Splicing Audit" overrideFontSize={"24px"} />
      <DevModeLabel />

      {auditCoords.latitude && auditCoords.longitude && state.questions && (
        <>
          <Form onSubmit={submitAuditHandler} noValidate={true}>
            <CurrentUser />
            <CurrentDate />
            <LatLong locationObject={auditCoords} />
            <FormPage1
              state={state}
              dispatch={dispatch}
              ACTIONS={ACTIONS}
              auditCoords={auditCoords}
            />
            {formPage1Completed() && (
              <>
                <FormPage2
                  state={state}
                  dispatch={dispatch}
                  ACTIONS={ACTIONS}
                  auditCoords={auditCoords}
                />
                <Switch
                  label={
                    state.showToolInventory
                      ? `Add Tool Inventory: Yes`
                      : `Add Tool Inventory: No`
                  }
                  identifier={"showToolInventory"}
                  isChecked={state.showToolInventory}
                  setIsChecked={switchToggleHandler}
                />
              </>
            )}
            {state.showToolInventory && (
              <ToolInventory
                state={state}
                dispatch={dispatch}
                ACTIONS={ACTIONS}
              />
            )}
          </Form>
          <CenterCard>
            <Button
              text={`Submit Audit`}
              onClick={submitAuditHandler}
              disabled={!state.formValid}
            />
          </CenterCard>
        </>
      )}

      {(!auditCoords?.latitude || !auditCoords?.longitude) && (
        <SectionHeader text="Form disabled, unable to determine location. Please try again." />
      )}

      <CenterCard>
        <Button onClick={cancelHandler} text="Cancel" />
      </CenterCard>

      {state.preview && (
        <ImagePreviewPrompt
          preview={state.preview}
          confirmHandler={() => setPreviewHandler(null)}
          deleteable={false}
        />
      )}

      <FullScreenActivityIndicator
        loading={loading}
        text="Fetching Question Data..."
      />
      <FullScreenActivityIndicator
        loading={state.loading}
        text="Submitting Audit..."
      />
      <InfoPrompt text={state.status} statusCleared={clearStatus} />
      <InfoPrompt text={state.submissionStatus} statusCleared={cancelHandler} />
    </>
  );
};

export default SplicerAudit;
